import { useState } from 'react';

import {
  useApi,
  discoveryApiRef,
  alertApiRef,
  DiscoveryApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import { OwnershipEntity, OwnershipHandlerResult } from '../types';

export interface JiraTeamOwnershipItem {
  jira_team_id: string;
  team_id?: string;
}

export interface JiraTeamOwnershipRequest {
  updates: JiraTeamOwnershipItem[];
}

export interface JiraTeamOwnershipResponse {
  data: Array<{
    jira_team_id: string;
    updated_owner_team_id?: string;
    success: boolean;
    message?: string;
  }>;
  total_requested: number;
  total_updated: number;
  failed: Array<{ jira_team_id: string; error: string }>;
  generated_at: string;
}

async function updateJiraTeamOwnership(
  discoveryApi: DiscoveryApi,
  request: JiraTeamOwnershipRequest,
): Promise<JiraTeamOwnershipResponse> {
  const baseUrl = await discoveryApi.getBaseUrl('watchtower');

  const response = await fetch(`${baseUrl}/jira-teams/ownership`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update Jira team ownership: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

async function refreshJiraTeams(
  discoveryApi: DiscoveryApi,
  jiraTeamIds: string[],
): Promise<{ updatedCount: number }> {
  const baseUrl = await discoveryApi.getBaseUrl('utility-api');

  const response = await fetch(
    `${baseUrl}/actions/refresh-jira-teams-ownership`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ jira_team_ids: jiraTeamIds }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to refresh Jira teams: ${response.status} ${response.statusText}. ${errorText}`,
    );
  }

  return response.json();
}

export function useJiraTeamOwnershipHandler(): OwnershipHandlerResult {
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const alertApi = useApi(alertApiRef);
  const [loading, setLoading] = useState(false);

  const submit = async (
    entities: OwnershipEntity[],
    ownerRef: string,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const fullTeamEntityRef = ownerRef.includes(':')
        ? ownerRef
        : `group:default/${ownerRef}`;

      const teamEntity = await catalogApi.getEntityByRef(fullTeamEntityRef);

      if (!teamEntity) {
        alertApi.post({
          message: `Team entity not found: ${fullTeamEntityRef}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      const ownerTeamId =
        fullTeamEntityRef.split('/').pop()?.toUpperCase() || '';
      const teamName = teamEntity.metadata.title || teamEntity.metadata.name;

      const updates: JiraTeamOwnershipItem[] = [];
      const jiraTeamIds: string[] = [];

      for (const entity of entities) {
        const jiraTeamId = entity.annotations?.['tomtom.com/jira-team-id'];

        if (!jiraTeamId) {
          alertApi.post({
            message: `Jira team "${entity.name}" is missing tomtom.com/jira-team-id annotation`,
            severity: 'error',
          });
          setLoading(false);
          return false;
        }

        jiraTeamIds.push(jiraTeamId);
        updates.push({
          jira_team_id: jiraTeamId,
          team_id: ownerTeamId,
        });
      }

      const updateResult = await updateJiraTeamOwnership(discoveryApi, {
        updates,
      });

      if (updateResult.failed.length > 0) {
        alertApi.post({
          message: `Failed to update ownership for ${
            updateResult.failed.length
          } Jira teams: ${updateResult.failed.map(f => f.error).join(', ')}`,
          severity: 'error',
        });
        setLoading(false);
        return false;
      }

      await refreshJiraTeams(discoveryApi, jiraTeamIds);

      const message =
        entities.length === 1
          ? `Successfully assigned ownership of Jira team to ${teamName}`
          : `Successfully assigned ownership for ${entities.length} Jira teams to ${teamName}`;

      alertApi.post({
        message,
        severity: 'success',
        display: 'transient',
      });

      setLoading(false);
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      alertApi.post({
        message: `Failed to update Jira team ownership: ${errorMessage}`,
        severity: 'error',
      });
      setLoading(false);
      return false;
    }
  };

  return {
    submit,
    loading,
  };
}
